import { FC, useEffect, useMemo, useState } from 'react';
import {
  DataVersionStatus,
  Detail,
  ErrorAlertWithDrawer,
  formatCodeName,
  formatDate,
  formatDateTimeShort,
  getDatePeriodString,
  ISODate,
  OutputTestValidationDetailsType,
  OutputTestValidationType,
  Padding,
  PopupView,
  Spinner,
  TotalEntries,
  useMutateTestValidateDagQuery,
  useMutationErrors,
  useQueryParams,
  useSystemClassifierItemById,
  useTestValidateQuery,
  ValidationRuleTestValidationType,
  VersionDisplay,
  VersionStatus,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import styled from 'styled-components';
import { Flex } from 'antd';
import translations from './OutputDataTestValidationReport.translations.json';
import { ValidationErrorsTable } from '../errorReport/ValidationErrorsTable';

const { addTranslations, t } = i18nNsFactory(
  'output-data-test-validation-error-report',
);
addTranslations(translations);

export const OutputDataTestValidationReport: FC = () => {
  const [{ validationRuleId, valueDate, outputDataVersionId }] =
    useQueryParams();
  const validationParams: ValidationRuleTestValidationType = useMemo(
    () => ({
      validationRuleId: Number(validationRuleId),
      valueDate: valueDate as ISODate,
      outputDataVersionId: Number(outputDataVersionId),
    }),
    [outputDataVersionId, validationRuleId, valueDate],
  );

  const { mutateAsync: testValidate } = useMutateTestValidateDagQuery({
    throwOnError: true,
  });
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors();

  const [testValidationDetails, setTestValidationDetails] =
    useState<OutputTestValidationDetailsType | null>(null);

  const {
    data: testValidation,
    isLoading: isTestValidating,
    error: testValidationError,
  } = useTestValidateQuery(testValidationDetails?.dagRunId ?? '', {
    enabled: !!testValidationDetails?.dagRunId,
  });

  useEffect(() => {
    clearMutationErrors();
    const run = async () => {
      try {
        const responseData = await testValidate({
          data: validationParams,
        });
        if (!responseData) return;
        setTestValidationDetails(responseData);
      } catch (error: any) {
        setMutationErrors(error.response?.data.errors ?? []);
      }
    };
    run();
  }, [clearMutationErrors, validationParams, setMutationErrors, testValidate]);

  useEffect(() => {
    if (testValidationError) {
      setMutationErrors(testValidationError.response?.data.errors ?? []);
    }
  }, [setMutationErrors, testValidationError]);

  return (
    <PopupView
      title={t('validationTestReport')}
      actions={
        testValidationDetails && (
          <VersionDisplay
            versionNumber={testValidationDetails.versionNumber}
            status={testValidationDetails.statusCode as VersionStatus}
          >
            {getDatePeriodString(
              testValidationDetails.validPeriod[0],
              testValidationDetails.validPeriod[1],
            )}
          </VersionDisplay>
        )
      }
      contentId="output-test-validation-report-main-content"
      footer={
        testValidationDetails && (
          <TotalEntries count={testValidation?.validationErrors.length || 0} />
        )
      }
      isLoading={testValidationDetails === null}
    >
      {testValidationDetails && (
        <>
          <ErrorAlertWithDrawer
            errorAction={t('validation')}
            validationMessagesMap={mutationErrors}
          />
          <Padding>
            <TestValidationDetails
              testValidationDetails={testValidationDetails}
              testValidation={testValidation}
            />
          </Padding>
          <Padding horizontal>
            {isTestValidating ? (
              <StyledSpinner message={t('loading')} />
            ) : (
              <Flex vertical gap="large">
                <ValidationErrorsTable
                  validationErrors={testValidation?.validationErrors || []}
                  scrollContainerSelector="#output-test-validation-report-main-content"
                />
              </Flex>
            )}
          </Padding>
        </>
      )}
    </PopupView>
  );
};

const TestValidationDetails = ({
  testValidationDetails,
  testValidation,
}: {
  testValidationDetails: OutputTestValidationDetailsType;
  testValidation?: OutputTestValidationType;
}) => {
  const {
    validationRuleCode,
    valueDate,
    queueTime,
    outputDataVersionNumber,
    outputDataVersionMinorNumber,
    outputDataVersionStatusId,
  } = testValidationDetails;
  const { startTime, endTime } = testValidation || {};
  const outputDataVersionStatus = useSystemClassifierItemById(
    'DATA_OBJECT_VER_STATUS',
    outputDataVersionStatusId,
  )?.code as DataVersionStatus;

  return (
    <>
      <Padding bottom>
        <h4>{formatCodeName(testValidationDetails)}</h4>
      </Padding>
      <Flex vertical gap="small">
        <Detail label={t('field.validationRule')}>{validationRuleCode}</Detail>
        <Detail label={t('field.valueDate')}>{formatDate(valueDate)}</Detail>
        <Detail label={t('field.outputDataVersion')}>
          <VersionDisplay
            versionNumber={outputDataVersionNumber}
            minorNumber={outputDataVersionMinorNumber}
            status={outputDataVersionStatus}
          >
            <DataVersionStatus status={outputDataVersionStatus} />
          </VersionDisplay>
        </Detail>
        <Detail label={t('field.queueTime')}>
          {formatDateTimeShort(queueTime)}
        </Detail>
        {testValidation && (
          <>
            <Detail label={t('field.startTime')}>
              {startTime ? formatDateTimeShort(startTime) : '-'}
            </Detail>
            <Detail label={t('field.endTime')}>
              {endTime ? formatDateTimeShort(endTime) : '-'}
            </Detail>
          </>
        )}
      </Flex>
    </>
  );
};

const StyledSpinner = styled(Spinner)`
  position: fixed;
  width: 100%;
  height: 100%;
  z-index: 1;
`;
